import { useState, useEffect, useRef, useCallback } from 'react';
import useTouchGesture from '../hooks/useTouchGesture';
import {
  DndContext, closestCenter,
  KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatDate, fromDateKey, toDateKey } from '../utils';
import ExportMenu from './ExportMenu';

// ─── SWIMLANE (GANTT) TIMELINE COMPONENT ─────────────────────────────────────
//
//  Renders events across parallel horizontal tracks grouped by the event's
//  `swimlane` field.  Events with no swimlane appear in "General".
//
//  Props:
//    events          {array}    list of event objects
//    colors          {object}   { primary, secondary, tertiary }
//    onEventClick    {function} receives the clicked event object
//    manualOrder     {boolean}
//    swimlanes       {array}    ordered list of swimlane names (from timeline)
//    swimlaneColors  {object}   { laneName: '#hexcolor', ... }
//    onReorderLanes  {function} called with new lane order array when user drags
//

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const LABEL_W    = 160;
const BAR_H      = 22;
const BAR_GAP    = 5;
const TRACK_PAD  = 10;
const MAX_STACK  = 4;
const MIN_BAR_W  = 12;
const AXIS_H     = 44;
const MS_SIZE    = 10;

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const PALETTE = [
  '#4A8FD4','#D96B3B','#5AA35C','#9059A8','#D4A83B',
  '#1A9E8C','#C94444','#5B8FD4','#3FAF70','#C04A9A',
];

// ─── DATE HELPERS ─────────────────────────────────────────────────────────────

const keyToDays = (key) => {
  if (!key) return 0;
  const { year, month, day, era } = fromDateKey(key);
  const astro = era === 'BC' ? 1 - year : year;
  return astro * 365.25 + (month || 1) * 30.44 + (day || 1);
};

// ─── AXIS TICK GENERATION ─────────────────────────────────────────────────────

const getAxisTicks = (minKey, maxKey, chartW) => {
  if (!minKey || !maxKey || chartW <= 0) return [];

  const minD = keyToDays(minKey);
  const maxD = keyToDays(maxKey);
  const span = Math.max(1, maxD - minD);
  const px   = (d) => Math.round(((d - minD) / span) * chartW);

  const { year: sy, month: sm, day: sd, era: se } = fromDateKey(minKey);
  const { year: ey, month: em, era: ee }           = fromDateKey(maxKey);
  const startA = se === 'BC' ? 1 - sy : sy;
  const endA   = ee === 'BC' ? 1 - ey : ey;

  const ticks = [];
  const push = (key, label) => {
    const d = keyToDays(key);
    if (d >= minD - 1 && d <= maxD + 365) ticks.push({ x: px(d), label });
  };

  if (span < 90) {
    const step = span < 8 ? 1 : span < 30 ? 7 : 14;
    const start = new Date(sy, (sm || 1) - 1, sd || 1);
    for (let off = 0; off <= span + step; off += step) {
      const d = new Date(start.getTime() + off * 86400000);
      const dayVal = minD + off;
      if (dayVal > maxD + step) break;
      ticks.push({ x: px(dayVal), label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) });
    }
  } else if (span < 730) {
    let y = startA; let m = sm || 1;
    const eLimit = endA * 12 + (em || 12);
    let count = 0;
    while (y * 12 + m <= eLimit + 1 && count++ < 300) {
      const era = y >= 1 ? 'AD' : 'BC';
      const dy  = y >= 1 ? y : 1 - y;
      const lbl = span < 200
        ? `${MONTH_ABBR[m - 1]} ${dy}${era === 'BC' ? ' BC' : ''}`
        : (m === 1 ? `${dy}${era === 'BC' ? ' BC' : ''}` : MONTH_ABBR[m - 1]);
      push(toDateKey(dy, m, 1, era), lbl);
      if (++m > 12) { m = 1; y++; }
    }
  } else if (span < 7300) {
    for (let y = startA - 1; y <= endA + 1; y++) {
      if (y === 0) continue;
      const era = y > 0 ? 'AD' : 'BC';
      const dy  = y > 0 ? y : 1 - y;
      push(toDateKey(dy, 1, 1, era), `${dy}${era === 'BC' ? ' BC' : ''}`);
    }
  } else if (span < 73000) {
    const s10 = Math.floor(startA / 10) * 10;
    for (let y = s10; y <= endA + 10; y += 10) {
      if (y === 0) continue;
      const era = y > 0 ? 'AD' : 'BC';
      const dy  = y > 0 ? y : 1 - y;
      push(toDateKey(dy, 1, 1, era), `${dy}s${era === 'BC' ? ' BC' : ''}`);
    }
  } else {
    const s100 = Math.floor(startA / 100) * 100;
    for (let y = s100; y <= endA + 100; y += 100) {
      if (y === 0) continue;
      const era = y > 0 ? 'AD' : 'BC';
      const dy  = y > 0 ? y : 1 - y;
      push(toDateKey(dy, 1, 1, era), `${dy}${era === 'BC' ? ' BC' : ''}`);
    }
  }

  return ticks.filter((t, i, arr) => i === 0 || t.x - arr[i - 1].x >= 50);
};

// ─── OVERLAP DETECTION ───────────────────────────────────────────────────────

const assignRows = (evs) => {
  const s = [...evs].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
  const rowEnds = [];
  return s.map(ev => {
    let row = rowEnds.findIndex(end => end <= ev.startDate);
    if (row === -1) { row = rowEnds.length; rowEnds.push(ev.endDate); }
    else { if (ev.endDate > rowEnds[row]) rowEnds[row] = ev.endDate; }
    return { ...ev, _row: row };
  });
};

// ─── LANE HELPERS ─────────────────────────────────────────────────────────────

const getLaneOrder = (events, swimlanes) => {
  const seen = new Set();
  const fromEvts = [];
  events.forEach(e => {
    const k = e.swimlane || 'General';
    if (!seen.has(k)) { seen.add(k); fromEvts.push(k); }
  });
  if (!swimlanes || !swimlanes.length) return fromEvts;
  const merged = []; const ms = new Set();
  [...swimlanes, ...fromEvts].forEach(k => { if (!ms.has(k)) { ms.add(k); merged.push(k); } });
  return merged.filter(k => seen.has(k));
};

const trackH = (numRows, expanded) =>
  TRACK_PAD * 2 + Math.min(expanded ? numRows : Math.min(numRows, MAX_STACK), numRows) * (BAR_H + BAR_GAP) - BAR_GAP;

const laneColor = (name, idx, swimlaneColors) =>
  (swimlaneColors && swimlaneColors[name]) || PALETTE[idx % PALETTE.length];

// ─── GRIP ICON ────────────────────────────────────────────────────────────────

const GripDots = () => (
  <svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M9 5a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM15 5a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM9 12a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM15 12a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM9 19a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM15 19a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
  </svg>
);

// ─── SORTABLE TRACK ROW ───────────────────────────────────────────────────────
//
//  Defined at module level so React sees a stable identity across re-renders.
//  Renders one swimlane row: sticky label cell + scrollable chart cell.

const SortableTrackRow = ({
  lane, laneIdx, laneEntry, chartW, colors, axisTicks,
  hasDraggedRef, onEventClick, expandedTracks, onExpand,
  setTooltip, dateToX, canReorder,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: lane });

  const { events: lEvs, numRows, color } = laneEntry;
  const expanded  = expandedTracks.has(lane);
  const visRows   = expanded ? numRows : Math.min(numRows, MAX_STACK);
  const tH        = TRACK_PAD * 2 + visRows * (BAR_H + BAR_GAP) - BAR_GAP;
  const hiddenCt  = numRows > MAX_STACK && !expanded ? numRows - MAX_STACK : 0;
  const rowBg     = laneIdx % 2 === 0 ? 'var(--bg)' : 'var(--bg2)';
  const visEvts   = lEvs.filter(e => e._row < visRows);

  return (
    <div
      ref={setNodeRef}
      className="sl-track-row"
      style={{
        height: tH + (hiddenCt ? 28 : 0) + 1,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        zIndex: isDragging ? 10 : 'auto',
      }}
    >
      {/* Label cell — sticky */}
      <div className="sl-label-cell" style={{ background: rowBg }}>
        {/* Drag handle — only shown when reordering is available */}
        {canReorder && (
          <div
            {...attributes}
            {...listeners}
            aria-label={`Drag to reorder ${lane}`}
            title="Drag to reorder"
            onMouseDown={e => e.stopPropagation()} // prevent chart pan
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              color: 'var(--text3)',
              flexShrink: 0,
              alignSelf: 'flex-start',
              paddingTop: 2,
              marginRight: 2,
              touchAction: 'none',
            }}
          >
            <GripDots />
          </div>
        )}
        <div className="sl-lane-pip" style={{ background: color }} />
        <div className="sl-lane-info">
          <span className="sl-lane-name">{lane}</span>
          <span className="sl-lane-count" style={{ color: colors.secondary }}>
            {lEvs.length} event{lEvs.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Chart cell */}
      <div className="sl-chart-cell" style={{ width: chartW, background: rowBg }}>
        {/* Vertical grid lines */}
        {axisTicks.map((t, i) => (
          <div key={i} className="sl-grid-line" style={{ left: t.x, borderColor: `${colors.secondary}12` }} />
        ))}

        {/* Event bars and milestones */}
        {visEvts.map(ev => {
          const isMilestone = ev.startDate === ev.endDate;
          const x1 = dateToX(ev.startDate);
          const x2 = dateToX(ev.endDate);
          const barW   = Math.max(MIN_BAR_W, x2 - x1);
          const barTop = TRACK_PAD + ev._row * (BAR_H + BAR_GAP);
          const pct    = ev.progress != null ? Math.min(100, Math.max(0, ev.progress)) : null;

          const handleClick = (e) => {
            e.stopPropagation();
            if (!hasDraggedRef.current) onEventClick(ev);
          };
          const handleEnter = (e) => setTooltip({ ev, cx: e.clientX, cy: e.clientY });
          const handleLeave = () => setTooltip(null);
          const handleMove  = (e) => setTooltip(p => p ? { ...p, cx: e.clientX, cy: e.clientY } : null);

          if (isMilestone) {
            return (
              <button
                key={ev.id}
                className="sl-milestone"
                onClick={handleClick}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                onMouseMove={handleMove}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEventClick(ev); } }}
                aria-label={ev.title}
                style={{
                  position: 'absolute',
                  left:   x1 - MS_SIZE,
                  top:    barTop + BAR_H / 2 - MS_SIZE,
                  width:  MS_SIZE * 2,
                  height: MS_SIZE * 2,
                  background: color,
                  transform: 'rotate(45deg)',
                  borderRadius: 2,
                }}
              />
            );
          }

          const showLabel = barW > 50;
          return (
            <button
              key={ev.id}
              className="sl-bar"
              onClick={handleClick}
              onMouseEnter={handleEnter}
              onMouseLeave={handleLeave}
              onMouseMove={handleMove}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEventClick(ev); } }}
              aria-label={`${ev.title}${pct != null ? `, ${pct}% complete` : ''}`}
              tabIndex={0}
              style={{
                position: 'absolute',
                left:   x1,
                top:    barTop,
                width:  barW,
                height: BAR_H,
                background: color,
              }}
            >
              {pct !== null && <div className="sl-bar-progress" style={{ width: `${pct}%` }} />}
              {showLabel && (
                <span className="sl-bar-label">
                  {pct !== null ? `${pct}% · ` : ''}{ev.title}
                </span>
              )}
            </button>
          );
        })}

        {/* "+ N more" expand button */}
        {hiddenCt > 0 && (
          <button
            className="sl-more-btn"
            onClick={e => { e.stopPropagation(); onExpand(lane); }}
            style={{
              position: 'absolute',
              bottom: 4,
              left: 6,
              background: `${color}22`,
              border: `1px solid ${color}70`,
              color,
            }}
          >
            +{hiddenCt} more
          </button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const SwimlaneTimeline = ({
  events, colors, onEventClick, manualOrder = false,
  swimlanes = [], swimlaneColors = {}, onReorderLanes,
  timeline = null, isEmbed = false,
}) => {

  const [zoom,            setZoom]            = useState('overview');
  const [expandedTracks,  setExpandedTracks]  = useState(new Set());
  const [tooltip,         setTooltip]         = useState(null);
  const [containerW,      setContainerW]      = useState(900);
  const [cursor,          setCursor]          = useState('default');
  const [longPressMenu,   setLongPressMenu]   = useState(null); // { x, y }

  const scrollRef     = useRef(null);
  const rootRef       = useRef(null);
  const isPanRef      = useRef(false);
  const panStartRef   = useRef({ x: 0, scrollLeft: 0 });
  const hasDraggedRef = useRef(false);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const prevZoomRef = useRef('standard');
  const handleBeforeCapture = useCallback(async () => {
    prevZoomRef.current = zoom;
    setZoom('overview');
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  }, [zoom]);
  const handleAfterCapture = useCallback(() => {
    setZoom(prevZoomRef.current);
  }, []);

  // ── Measure container ─────────────────────────────────────────────────────
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => setContainerW(e.contentRect.width || 900));
    obs.observe(el);
    setContainerW(el.getBoundingClientRect().width || 900);
    return () => obs.disconnect();
  }, []);

  // ── Document-level pan tracking ───────────────────────────────────────────
  useEffect(() => {
    const onMove = (e) => {
      if (!isPanRef.current) return;
      const dx = e.clientX - panStartRef.current.x;
      if (Math.abs(dx) > 4) hasDraggedRef.current = true;
      if (scrollRef.current) scrollRef.current.scrollLeft = panStartRef.current.scrollLeft - dx;
    };
    const onUp = () => {
      if (!isPanRef.current) return;
      isPanRef.current = false;
      setCursor('default');
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
  }, []);

  // ── Close long-press menu on outside click ────────────────────────────────
  useEffect(() => {
    if (!longPressMenu) return;
    const close = () => setLongPressMenu(null);
    document.addEventListener('pointerdown', close, { once: true });
    return () => document.removeEventListener('pointerdown', close);
  }, [longPressMenu]);

  // ── Ctrl+scroll to zoom ───────────────────────────────────────────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const zs = ['overview', 'standard', 'detail'];
      const i  = zs.indexOf(zoom);
      if (e.deltaY < 0 && i < 2) setZoom(zs[i + 1]);
      if (e.deltaY > 0 && i > 0) setZoom(zs[i - 1]);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoom]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const sorted = [...events].sort((a, b) => {
    if (manualOrder) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    return (a.startDate || "").localeCompare(b.startDate || "");
  });

  if (!sorted.length) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
        No events yet. Add your first event.
      </div>
    );
  }

  const minDate = sorted[0].startDate;
  const maxDate = sorted[sorted.length - 1].endDate;
  const minD    = keyToDays(minDate);
  const maxD    = keyToDays(maxDate);
  const span    = Math.max(1, maxD - minD);

  const chartW_base = Math.max(1, containerW - LABEL_W - 2);
  const chartW = zoom === 'overview'
    ? chartW_base
    : Math.max(chartW_base, Math.ceil(span * (zoom === 'standard' ? 3 : 20)));

  const dateToX = (key) => Math.round(((keyToDays(key) - minD) / span) * chartW);

  // ── Today line ────────────────────────────────────────────────────────────
  const now = new Date();
  const todayKey = toDateKey(now.getFullYear(), now.getMonth() + 1, now.getDate(), 'AD');
  const todayX   = (todayKey >= minDate && todayKey <= maxDate) ? dateToX(todayKey) : null;

  // ── Lane data ─────────────────────────────────────────────────────────────
  const laneOrder = getLaneOrder(sorted, swimlanes);

  const laneData = {};
  laneOrder.forEach((lane, idx) => {
    const evs     = sorted.filter(e => (e.swimlane || 'General') === lane);
    const rowed   = assignRows(evs);
    const numRows = rowed.reduce((m, e) => Math.max(m, e._row + 1), 1);
    laneData[lane] = { events: rowed, numRows, color: laneColor(lane, idx, swimlaneColors) };
  });

  // ── Dependency arrows ─────────────────────────────────────────────────────
  const evPosMap = {};
  let laneTopAcc = 0;
  laneOrder.forEach(lane => {
    const { events: lEvs, numRows } = laneData[lane];
    const expanded = expandedTracks.has(lane);
    const tH = trackH(numRows, expanded);
    lEvs.forEach(ev => {
      const row = ev._row;
      if (row >= MAX_STACK && !expanded) return;
      const x1 = dateToX(ev.startDate);
      const x2 = Math.max(x1 + MIN_BAR_W, dateToX(ev.endDate));
      const y  = laneTopAcc + TRACK_PAD + row * (BAR_H + BAR_GAP) + BAR_H / 2;
      evPosMap[ev.id] = { x1, x2, y };
    });
    laneTopAcc += tH + 1;
  });

  const axisTicks = getAxisTicks(minDate, maxDate, chartW);

  const totalTracksH = laneOrder.reduce((h, lane) => {
    const { numRows } = laneData[lane];
    return h + trackH(numRows, expandedTracks.has(lane)) + 1;
  }, 0);

  // ── Pan handlers ──────────────────────────────────────────────────────────
  const onPanStart = (e) => {
    if (e.button !== 0) return;
    isPanRef.current  = true;
    hasDraggedRef.current = false;
    panStartRef.current = { x: e.clientX, scrollLeft: scrollRef.current?.scrollLeft || 0 };
    setCursor('grabbing');
  };

  // ── Lane drag end ─────────────────────────────────────────────────────────
  const handleLaneDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id || !onReorderLanes) return;
    const oldIdx = laneOrder.indexOf(active.id);
    const newIdx = laneOrder.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorderLanes(arrayMove(laneOrder, oldIdx, newIdx));
  };

  const canReorder = !!onReorderLanes;

  // ── Zoom levels in order for pinch cycling ───────────────────────────��────
  const ZS = ['overview', 'standard', 'detail'];

  // ── Touch gestures: pan + pinch zoom + long press ─────────────────────────
  useTouchGesture(scrollRef, {
    touchAction: 'pan-y',
    onSwipeLeft: (velocity) => {
      if (!scrollRef.current) return;
      let v = velocity * 100;
      const decay = () => {
        if (!scrollRef.current || Math.abs(v) < 0.5) return;
        scrollRef.current.scrollLeft += v;
        v *= 0.92;
        requestAnimationFrame(decay);
      };
      requestAnimationFrame(decay);
    },
    onSwipeRight: (velocity) => {
      if (!scrollRef.current) return;
      let v = -velocity * 100;
      const decay = () => {
        if (!scrollRef.current || Math.abs(v) < 0.5) return;
        scrollRef.current.scrollLeft += v;
        v *= 0.92;
        requestAnimationFrame(decay);
      };
      requestAnimationFrame(decay);
    },
    onPinch: (scale, _mx, _my) => {
      // Pinch cycles between zoom levels
      const cur = ZS.indexOf(zoom);
      if (scale < 0.85 && cur > 0)               setZoom(ZS[cur - 1]);
      else if (scale > 1.2 && cur < ZS.length - 1) setZoom(ZS[cur + 1]);
    },
    onLongPress: (x, y) => {
      setLongPressMenu({ x, y });
    },
  });

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div
      ref={rootRef}
      className="sl-root"
      style={{ '--sl-primary': colors.primary, '--sl-secondary': colors.secondary }}
    >

      {/* ── Zoom controls ── */}
      <div className="sl-zoom-bar">
        {['overview', 'standard', 'detail'].map(z => (
          <button
            key={z}
            className={`sl-zoom-btn${zoom === z ? ' sl-zoom-active' : ''}`}
            onClick={() => setZoom(z)}
            style={zoom === z ? { borderColor: colors.primary, color: colors.primary, background: `${colors.primary}18` } : {}}
          >
            {z.charAt(0).toUpperCase() + z.slice(1)}
          </button>
        ))}
        <span className="sl-zoom-hint">
          {canReorder ? 'Drag lane labels to reorder · ' : ''}Ctrl+scroll to zoom · drag chart to pan
        </span>
        {!isEmbed && timeline && (
          <ExportMenu
            timeline={timeline}
            captureRef={rootRef}
            onBeforeCapture={handleBeforeCapture}
            onAfterCapture={handleAfterCapture}
          />
        )}
      </div>

      {/* ── Scroll container ── */}
      <div ref={scrollRef} className="sl-scroll" onMouseDown={onPanStart} style={{ cursor }}>
        <div
          className="sl-canvas"
          style={{ minWidth: LABEL_W + chartW + 'px', position: 'relative' }}
        >

          {/* ── Axis header row ── */}
          <div className="sl-axis-row">
            <div className="sl-corner" style={{ width: LABEL_W, minWidth: LABEL_W }}>
              <span style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Track</span>
            </div>
            <div className="sl-axis-bar" style={{ width: chartW, position: 'relative', height: AXIS_H }}>
              {axisTicks.map((tick, i) => (
                <div key={i} className="sl-tick" style={{ left: tick.x }}>
                  <div className="sl-tick-line" style={{ borderColor: `${colors.secondary}50` }} />
                  <span className="sl-tick-label" style={{ color: colors.secondary }}>{tick.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Today line spanning all tracks ── */}
          {todayX !== null && (
            <div
              className="sl-today-line"
              style={{ left: LABEL_W + todayX, top: AXIS_H - 18, bottom: 0 }}
            >
              <span className="sl-today-badge">Today</span>
            </div>
          )}

          {/* ── Dependency arrows overlay ── */}
          <svg
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: LABEL_W,
              top: AXIS_H,
              width: chartW,
              height: totalTracksH,
              pointerEvents: 'none',
              overflow: 'visible',
              zIndex: 5,
            }}
          >
            {sorted.filter(ev => ev.dependsOn).map(ev => {
              const dep = evPosMap[ev.dependsOn];
              const cur = evPosMap[ev.id];
              if (!dep || !cur) return null;
              const x1 = dep.x2, y1 = dep.y;
              const x2 = cur.x1, y2 = cur.y;
              const mx = x1 + Math.max(20, (x2 - x1) * 0.5);
              return (
                <g key={ev.id}>
                  <path
                    d={`M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`}
                    fill="none"
                    stroke={colors.secondary}
                    strokeWidth="1.5"
                    strokeDasharray="5 3"
                    strokeOpacity="0.6"
                  />
                  <polygon
                    points={`${x2},${y2} ${x2 - 7},${y2 - 3.5} ${x2 - 7},${y2 + 3.5}`}
                    fill={colors.secondary}
                    fillOpacity="0.6"
                  />
                </g>
              );
            })}
          </svg>

          {/* ── Track rows (sortable) ── */}
          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleLaneDragEnd}
          >
            <SortableContext items={laneOrder} strategy={verticalListSortingStrategy}>
              {laneOrder.map((lane, laneIdx) => (
                <SortableTrackRow
                  key={lane}
                  lane={lane}
                  laneIdx={laneIdx}
                  laneEntry={laneData[lane]}
                  chartW={chartW}
                  colors={colors}
                  axisTicks={axisTicks}
                  hasDraggedRef={hasDraggedRef}
                  onEventClick={onEventClick}
                  expandedTracks={expandedTracks}
                  onExpand={(l) => setExpandedTracks(s => { const n = new Set(s); n.add(l); return n; })}
                  setTooltip={setTooltip}
                  dateToX={dateToX}
                  canReorder={canReorder}
                />
              ))}
            </SortableContext>
          </DndContext>

        </div>
      </div>

      {/* ── Tooltip ── */}
      {tooltip && (
        <div
          className="sl-tooltip"
          style={{ left: tooltip.cx + 14, top: tooltip.cy + 14 }}
        >
          <div className="sl-tooltip-title">{tooltip.ev.title}</div>
          <div className="sl-tooltip-dates">
            {formatDate(tooltip.ev.startDate, tooltip.ev.startEra !== 'NONE')}
            {tooltip.ev.startDate !== tooltip.ev.endDate && (
              <> — {formatDate(tooltip.ev.endDate, tooltip.ev.endEra !== 'NONE')}</>
            )}
          </div>
          <div className="sl-tooltip-lane">{tooltip.ev.swimlane || 'General'}</div>
          {tooltip.ev.progress != null && (
            <div className="sl-tooltip-progress">Progress: {tooltip.ev.progress}%</div>
          )}
        </div>
      )}

      {/* ── Long-press context menu ── */}
      {longPressMenu && (
        <div
          className="sl-context-menu"
          style={{ left: longPressMenu.x, top: longPressMenu.y }}
          onPointerDown={e => e.stopPropagation()}
        >
          {todayX !== null && (
            <button
              className="sl-context-item"
              onClick={() => {
                if (scrollRef.current) {
                  scrollRef.current.scrollLeft = Math.max(0, LABEL_W + todayX - scrollRef.current.clientWidth / 2);
                }
                setLongPressMenu(null);
              }}
            >
              Jump to Today
            </button>
          )}
          <button
            className="sl-context-item"
            onClick={() => {
              setZoom('overview');
              if (scrollRef.current) scrollRef.current.scrollLeft = 0;
              setLongPressMenu(null);
            }}
          >
            Fit All
          </button>
        </div>
      )}

    </div>
  );
};

export default SwimlaneTimeline;
